import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentEvents,
  GetProjectCharterResponse,
  GetProjectPlanResponse,
  ProjectCharterView,
  ProjectPlanStep,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import api from '../../api';
import ArchetypeIcon from './ArchetypeIcon';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ArchiveIcon } from '../../icons/empty-archive.svg';
import { ReactComponent as SunSettingIcon } from '../../icons/sun-setting.svg';
import { ReactComponent as WhatsappIcon } from '../../icons/whatsapp-color.svg';
import { ReactComponent as ChevronDownIcon } from '../../icons/chevron-down.svg';
import { ReactComponent as CheckCircleIcon } from '../../icons/check-circle.svg';
import { ReactComponent as RefreshArrowsIcon } from '../../icons/refresh-arrows.svg';
import './project-detail-view.scss';

interface ProjectDetailViewProps {
  threadId: string;
  projectTitle?: string;
  onBack: () => void;
  /** Called once the project has been archived, so the parent can navigate
   * back to the home screen where the project no longer appears. */
  onArchived?: () => void;
}

type DetailTab = 'activity' | 'working';

// Number of most-recent completed steps to keep visible. Anything completed
// before these is collapsed behind the top reveal control.
const VISIBLE_COMPLETED = 2;

// Number of upcoming steps (after the current one) to keep visible. Anything
// further out is collapsed behind the bottom reveal control.
const VISIBLE_UPCOMING = 4;

// Inline pill shown when text mentions the Daily Briefing.
const DailyBriefingPill: React.FC = () => (
  <span className='pdv-pill pdv-pill--briefing'>
    <SunSettingIcon className='pdv-pill-icon' />
    <span className='pdv-pill-text'>Daily Briefing</span>
  </span>
);

// Inline pill shown when text mentions WhatsApp.
const WhatsappPill: React.FC = () => (
  <span className='pdv-pill pdv-pill--whatsapp'>
    <WhatsappIcon className='pdv-pill-icon' />
    <span className='pdv-pill-text'>WhatsApp</span>
  </span>
);

const SPECIAL_WORDS = /(Daily Briefing|WhatsApp)/i;

/**
 * Render step text with **bold** markdown and special-word styling for
 * "Daily Briefing" and "WhatsApp" (mirrors how Routines styles Daily Briefing).
 */
function renderWithSpecialWords(text: string): React.ReactNode {
  const boldParts = text.split(/(\*\*[^*]+\*\*)/);
  return boldParts.map((part, i) => {
    const isBold = part.startsWith('**') && part.endsWith('**');
    const inner = isBold ? part.slice(2, -2) : part;

    const segments = inner.split(SPECIAL_WORDS);
    const rendered = segments.map((seg, j) => {
      if (/^Daily Briefing$/i.test(seg)) {
        return <DailyBriefingPill key={`${i}-${j}`} />;
      }
      if (/^WhatsApp$/i.test(seg)) {
        return <WhatsappPill key={`${i}-${j}`} />;
      }
      return <React.Fragment key={`${i}-${j}`}>{seg}</React.Fragment>;
    });

    return isBold ? (
      <strong key={i}>{rendered}</strong>
    ) : (
      <React.Fragment key={i}>{rendered}</React.Fragment>
    );
  });
}

/**
 * Turn a cadence interval in minutes into a friendly frequency word for the
 * Updates cards ("daily", "weekly", "every 3 days", ...).
 */
function formatFrequency(minutes: number): string {
  const days = Math.max(1, Math.round(minutes / 1440));
  if (days === 1) return 'daily';
  if (days === 7) return 'weekly';
  if (days >= 28 && days <= 31) return 'monthly';
  return `every ${days} days`;
}

type StepStatus = 'completed' | 'current' | 'upcoming';

interface IndexedStep {
  step: ProjectPlanStep;
  index: number;
}

const ISO_DATE = /\b(\d{4})-(\d{2})-(\d{2})\b/g;

/**
 * Replace any ISO dates (YYYY-MM-DD) in a due-date string with a friendly
 * en-GB format ("10 Jul", with the year only when it isn't the current one).
 * Free-text due dates like "When destination is chosen" pass through as-is.
 */
function formatDueDate(dueDate: string): string {
  return dueDate.replace(ISO_DATE, (match, y, m, d) => {
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    if (isNaN(date.getTime())) return match;
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      ...(date.getFullYear() !== new Date().getFullYear() && {
        year: 'numeric',
      }),
    });
  });
}

// Sub-step row inside an expanded step: check state, description, and an
// optional due-date pill (mirroring the pills used in ProjectPlanChecklist).
const SubStepRow: React.FC<{ child: ProjectPlanStep }> = ({ child }) => (
  <li className={`pdv-subitem${child.checked ? ' pdv-subitem--done' : ''}`}>
    <CheckCircleIcon className='pdv-subitem-icon' />
    <div className='pdv-subitem-body'>
      <span className='pdv-subitem-text'>
        {renderWithSpecialWords(child.description)}
      </span>
      {child.dueDate && (
        <span className='pdv-subitem-tags'>
          <span className='pdv-subpill pdv-subpill--due'>
            {formatDueDate(child.dueDate)}
          </span>
        </span>
      )}
    </div>
  </li>
);

interface StepRowProps {
  step: ProjectPlanStep;
  status: StepStatus;
  expanded?: boolean;
  onToggle?: () => void;
}

const StepRow: React.FC<StepRowProps> = ({
  step,
  status,
  expanded,
  onToggle,
}) => {
  const children = step.children || [];
  const expandable = children.length > 0;
  const completedChildren = children.filter((c) => c.checked).length;
  const isOpen = expandable && !!expanded;

  const rowInner = (
    <>
      {status === 'current' ? (
        <RefreshArrowsIcon className='pdv-item-icon' />
      ) : (
        <CheckCircleIcon className='pdv-item-icon' />
      )}
      <span className='pdv-item-text'>
        {renderWithSpecialWords(step.description)}
      </span>
      {expandable && (
        <span className='pdv-item-meta'>
          <span className='pdv-item-count'>
            {completedChildren}/{children.length}
          </span>
          <ChevronDownIcon className='pdv-item-chevron' />
        </span>
      )}
    </>
  );

  return (
    <li
      className={`pdv-item pdv-item--${status}${isOpen ? ' pdv-item--expanded' : ''}`}
    >
      {expandable ? (
        <button
          type='button'
          className='pdv-item-row pdv-item-row--btn'
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          {rowInner}
        </button>
      ) : (
        <div className='pdv-item-row'>{rowInner}</div>
      )}
      {isOpen && (
        <ul className='pdv-subitems'>
          {children.map((child, i) => (
            <SubStepRow key={i} child={child} />
          ))}
        </ul>
      )}
    </li>
  );
};

interface RevealRowProps {
  count: number;
  kind: 'completed' | 'upcoming';
  open: boolean;
  onToggle: () => void;
}

const RevealRow: React.FC<RevealRowProps> = ({
  count,
  kind,
  open,
  onToggle,
}) => (
  <li className='pdv-item pdv-item--reveal'>
    <button
      type='button'
      className={`pdv-item-reveal-btn ${open ? 'open' : ''} pdv-item-reveal-btn--${kind}`}
      onClick={onToggle}
    >
      {open
        ? `Hide ${kind} steps`
        : `+${count} more ${kind} ${count === 1 ? 'step' : 'steps'}`}
      <ChevronDownIcon className='pdv-item-reveal-icon' />
    </button>
  </li>
);

const ProjectDetailView: React.FC<ProjectDetailViewProps> = ({
  threadId,
  projectTitle,
  onBack,
  onArchived,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [activeTab, setActiveTab] = useState<DetailTab>('activity');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [steps, setSteps] = useState<ProjectPlanStep[]>([]);
  const [summary, setSummary] = useState<string | undefined>(undefined);
  const [charter, setCharter] = useState<ProjectCharterView | null>(null);
  const [loading, setLoading] = useState(true);
  const [charterLoading, setCharterLoading] = useState(true);
  const [revealCompleted, setRevealCompleted] = useState(false);
  const [revealUpcoming, setRevealUpcoming] = useState(false);
  // Manual expand/collapse overrides, keyed by the step's index in the plan.
  // Steps without an override fall back to the default: expanded when current.
  const [stepToggles, setStepToggles] = useState<Record<number, boolean>>({});

  const fetchPlan = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetProjectPlanResponse>(
        `/project-plans/${encodeURIComponent(threadId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success && res.data.plan) {
        setSteps(res.data.plan.steps);
        setSummary(res.data.plan.summary);
      } else {
        setSteps([]);
        setSummary(undefined);
      }
    } catch {
      // A project without a plan yet returns 404 — that's an empty state, not
      // an error worth surfacing.
      setSteps([]);
      setSummary(undefined);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, threadId]);

  const fetchCharter = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetProjectCharterResponse>(
        `/project-charters/${encodeURIComponent(threadId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCharter(
        res.data.success && res.data.charter ? res.data.charter : null,
      );
    } catch {
      // A project without a charter yet (kickoff pending) returns 404 — an
      // empty state, not an error worth surfacing.
      setCharter(null);
    } finally {
      setCharterLoading(false);
    }
  }, [getAccessTokenSilently, threadId]);

  // Archive this project. The backend stops all associated activity
  // (heartbeats, sub-threads, subscriptions) and removes it from the home
  // list; we then hand control back to the parent to navigate home.
  const handleArchive = useCallback(async () => {
    if (isArchiving) return;
    setIsArchiving(true);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/agents/threads/archive',
        { threadId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowArchiveConfirm(false);
      if (onArchived) {
        onArchived();
      } else {
        onBack();
      }
    } catch (err) {
      console.error('Failed to archive project:', err);
      setIsArchiving(false);
    }
  }, [isArchiving, getAccessTokenSilently, threadId, onArchived, onBack]);

  useEffect(() => {
    fetchPlan();
    fetchCharter();
  }, [fetchPlan, fetchCharter]);

  // Refresh the plan live when the agent updates it.
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  useEffect(() => {
    if (!userChannel) return;
    const refresh = () => {
      fetchPlan();
      fetchCharter();
    };
    userChannel.bind(AgentEvents.PROJECT_PLAN_UPDATED, refresh);
    return () => {
      userChannel.unbind(AgentEvents.PROJECT_PLAN_UPDATED, refresh);
    };
  }, [userChannel, fetchPlan, fetchCharter]);

  // Split into completed / current / upcoming, preserving plan order. The
  // most recent completed steps stay visible; older ones collapse behind the
  // top reveal. The first unchecked step is "current" (highlighted); upcoming
  // steps beyond the visible window collapse behind the bottom reveal.
  const {
    olderCompleted,
    recentCompleted,
    currentStep,
    visibleUpcoming,
    hiddenUpcoming,
  } = useMemo(() => {
    const indexed: IndexedStep[] = steps.map((step, index) => ({
      step,
      index,
    }));
    const completed = indexed.filter(({ step }) => step.checked);
    const active = indexed.filter(({ step }) => !step.checked);
    const upcoming = active.slice(1);
    return {
      olderCompleted: completed.slice(0, -VISIBLE_COMPLETED),
      recentCompleted: completed.slice(-VISIBLE_COMPLETED),
      currentStep: active[0],
      visibleUpcoming: upcoming.slice(0, VISIBLE_UPCOMING),
      hiddenUpcoming: upcoming.slice(VISIBLE_UPCOMING),
    };
  }, [steps]);

  // The current step is always auto-expanded: whenever the step that's
  // current changes (e.g. the previous one completes), drop any manual
  // toggle on it so the expanded default applies again.
  const currentIndex = currentStep?.index;
  useEffect(() => {
    if (currentIndex === undefined) return;
    setStepToggles((prev) => {
      if (!(currentIndex in prev)) return prev;
      const next = { ...prev };
      delete next[currentIndex];
      return next;
    });
  }, [currentIndex]);

  const isExpanded = (index: number, status: StepStatus) =>
    stepToggles[index] ?? status === 'current';

  const toggleStep = (index: number, status: StepStatus) =>
    setStepToggles((prev) => ({
      ...prev,
      [index]: !(prev[index] ?? status === 'current'),
    }));

  const renderStep = ({ step, index }: IndexedStep, status: StepStatus) => (
    <StepRow
      key={`step-${index}`}
      step={step}
      status={status}
      expanded={isExpanded(index, status)}
      onToggle={() => toggleStep(index, status)}
    />
  );

  const hasPlan = steps.length > 0;

  const renderCurrentActivity = () => (
    <>
      <div className='pdv-section'>
        <p className='pdv-section-title'>Status update</p>
        <div className='pdv-card'>
          <p className='pdv-card-body'>
            {summary ||
              'Status updates for your projects will appear here soon.'}
          </p>
        </div>
      </div>

      <div className='pdv-section'>
        <p className='pdv-section-title'>Progress</p>

        {loading ? null : !hasPlan ? (
          <p className='pdv-empty'>
            Your project plan will appear here once your secretary has set it
            up.
          </p>
        ) : (
          <ul className='pdv-items'>
            {olderCompleted.length > 0 && (
              <RevealRow
                count={olderCompleted.length}
                kind='completed'
                open={revealCompleted}
                onToggle={() => setRevealCompleted((o) => !o)}
              />
            )}

            {revealCompleted &&
              olderCompleted.map((s) => renderStep(s, 'completed'))}

            {recentCompleted.map((s) => renderStep(s, 'completed'))}

            {currentStep && renderStep(currentStep, 'current')}

            {visibleUpcoming.map((s) => renderStep(s, 'upcoming'))}

            {revealUpcoming &&
              hiddenUpcoming.map((s) => renderStep(s, 'upcoming'))}

            {hiddenUpcoming.length > 0 && (
              <RevealRow
                count={hiddenUpcoming.length}
                kind='upcoming'
                open={revealUpcoming}
                onToggle={() => setRevealUpcoming((o) => !o)}
              />
            )}
          </ul>
        )}
      </div>
    </>
  );

  const renderHowImWorking = () => {
    if (charterLoading) return null;
    if (!charter) {
      return (
        <div className='pdv-section'>
          <p className='pdv-section-title'>Mission</p>
          <p className='pdv-empty'>
            How I&apos;m working on this project will appear here once
            we&apos;ve agreed the mission together.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className='pdv-section'>
          <p className='pdv-section-title'>Mission</p>
          <div className='pdv-card pdv-mission-card'>
            <p className='pdv-card-body'>{charter.mission}</p>
            <div className='pdv-role'>
              <p className='pdv-role-title'>My role</p>
              <div className='pdv-role-row'>
                <span className='pdv-role-icon'>
                  <ArchetypeIcon archetype={charter.archetype} />
                </span>
                <p className='pdv-role-text'>
                  I&apos;ll act as a <strong>{charter.archetypeLabel}</strong>.{' '}
                  {charter.roleDescription}
                </p>
              </div>
            </div>
          </div>
        </div>

        {(charter.updateIntervalMinutes != null ||
          charter.checkInIntervalMinutes != null) && (
          <div className='pdv-section'>
            <p className='pdv-section-title'>Updates</p>
            <div className='pdv-updates'>
              {charter.updateIntervalMinutes != null && (
                <div className='pdv-card pdv-update-card'>
                  <p className='pdv-update-text'>
                    I&apos;ll update you on progress{' '}
                    {formatFrequency(charter.updateIntervalMinutes)} in your
                  </p>
                  <DailyBriefingPill />
                </div>
              )}
              {charter.checkInIntervalMinutes != null && (
                <div className='pdv-card pdv-update-card'>
                  <p className='pdv-update-text'>
                    I&apos;ll ask you for your progress{' '}
                    {formatFrequency(charter.checkInIntervalMinutes)} in your
                  </p>
                  <DailyBriefingPill />
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className='pdv'>
      <div className='pdv-header'>
        <div className='pdv-header-top'>
          <button type='button' className='pdv-back' onClick={onBack}>
            <ArrowLeftIcon className='pdv-back-icon' />
            Back
          </button>
        </div>
        <p className='pdv-eyebrow'>Long-Term Focused Work</p>
        <h1 className='pdv-title'>{projectTitle || 'Project'}</h1>
      </div>

      <div className='pdv-tabs' role='tablist'>
        <button
          type='button'
          role='tab'
          aria-selected={activeTab === 'activity'}
          className={`pdv-tab ${activeTab === 'activity' ? 'pdv-tab--active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          Current activity
        </button>
        <button
          type='button'
          role='tab'
          aria-selected={activeTab === 'working'}
          className={`pdv-tab ${activeTab === 'working' ? 'pdv-tab--active' : ''}`}
          onClick={() => setActiveTab('working')}
        >
          How I&apos;m working
        </button>
        <div className='pdv-archive-wrap'>
          <button
            type='button'
            className='pdv-archive'
            onClick={() => setShowArchiveConfirm(true)}
            disabled={isArchiving}
            aria-label='Archive this work'
          >
            <ArchiveIcon className='pdv-archive-icon' />
          </button>
          <span className='pdv-archive-tooltip' role='tooltip'>
            Archive this work
          </span>
        </div>
      </div>

      <div className='pdv-content'>
        {activeTab === 'activity'
          ? renderCurrentActivity()
          : renderHowImWorking()}
      </div>

      {showArchiveConfirm && (
        <div
          className='pdv-archive-overlay'
          onClick={() => !isArchiving && setShowArchiveConfirm(false)}
        >
          <div
            className='pdv-archive-dialog'
            role='dialog'
            aria-modal='true'
            aria-labelledby='pdv-archive-title'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='pdv-archive-dialog-header'>
              <h2 id='pdv-archive-title' className='pdv-archive-dialog-title'>
                Archive this work?
              </h2>
              <button
                type='button'
                className='pdv-archive-dialog-close'
                aria-label='Close'
                onClick={() => setShowArchiveConfirm(false)}
                disabled={isArchiving}
              >
                ×
              </button>
            </div>
            <p className='pdv-archive-dialog-body'>
              You&apos;re about to archive this work. All activity associated
              with this work will be stopped, and it&apos;ll be hidden from the
              home screen. This action cannot be undone.
            </p>
            <div className='pdv-archive-dialog-actions'>
              <button
                type='button'
                className='pdv-archive-dialog-cancel'
                onClick={() => setShowArchiveConfirm(false)}
                disabled={isArchiving}
              >
                Cancel
              </button>
              <button
                type='button'
                className='pdv-archive-dialog-confirm'
                onClick={handleArchive}
                disabled={isArchiving}
              >
                {isArchiving ? 'Archiving…' : 'Yes, archive'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailView;
